'use client';

import React, { useState } from 'react';
import { QuickCalculatorForm } from '@/components/quick-calculator/QuickCalculatorForm';
import { calculateShadbala, ShadbalaResult, CreateBirthProfileInput } from '@/lib/api/client';
import { CalculatorPageContent } from '@/components/calculators/CalculatorPageContent';
import { Scale } from 'lucide-react';

export default function ShadbalaCalculatorPage() {
  const [result, setResult] = useState<ShadbalaResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCalculate = async (input: CreateBirthProfileInput) => {
    setLoading(true);
    setError(null);
    try {
      const data = await calculateShadbala(input);
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Failed to calculate Shadbala strength');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const introParagraph = "Shadbala is the classical 6-fold planetary strength assessment system in Parashari Vedic astrology. It quantifies a planet's true capacity to deliver results in Virupas (60 Virupas = 1 Rupa) across six independent astronomical dimensions.";

  const methodologyContent = (
    <div className="space-y-3">
      <p>
        I evaluate all 6 complete components of Shadbala using C-native Swiss Ephemeris (pyswisseph) routines under Lahiri Ayanamsha:
      </p>
      <ul className="list-disc list-inside space-y-1 text-xs text-[var(--muted)]">
        <li><strong>Sthana Bala</strong> (Positional: exalted, own sign, sign division strength)</li>
        <li><strong>Dig Bala</strong> (Directional: 1st, 4th, 7th, 10th house direction)</li>
        <li><strong>Kala Bala</strong> (Temporal: day/night, lunar phase, planetary hour)</li>
        <li><strong>Chesta Bala</strong> (Motional: retrograde speed and acceleration)</li>
        <li><strong>Naisargika Bala</strong> (Natural: fixed inherent brightness)</li>
        <li><strong>Drik Bala</strong> (Aspectual: beneficed vs malefic planetary aspects)</li>
      </ul>
    </div>
  );

  const subsections = [
    {
      title: "Understanding Rupa Thresholds",
      content: "Parashari texts define specific minimum Rupa requirements for each planet (e.g. Sun needs 6.5 Rupas, Moon 6.0 Rupas). Planets meeting their threshold are capable of delivering full positive potential during their Dasha."
    },
    {
      title: "Why High Positional Bala Isn't Enough",
      content: "A planet exalted in Sthana Bala may be weak in Dig Bala (wrong house direction) or Drik Bala (afflicted by malefic aspects). Shadbala combines all 6 components to reveal net effective potency."
    },
    {
      title: "Applying Shadbala in Dasha Analysis",
      content: "During a Mahadasha or Antardasha, the active planet's Shadbala score indicates whether its promises will manifest easily or require heavy effort."
    }
  ];

  const faqs = [
    {
      q: "What is Shadbala?",
      a: "Shadbala means 'six-fold strength'. It is a mathematical system in Vedic astrology that evaluates planetary potency across 6 distinct astronomical components."
    },
    {
      q: "What are the 6 components of Shadbala?",
      a: "The 6 components are Sthana Bala (Positional), Dig Bala (Directional), Kala Bala (Temporal), Chesta Bala (Motional), Naisargika Bala (Natural), and Drik Bala (Aspectual)."
    },
    {
      q: "What is a Rupa in Shadbala?",
      a: "A Rupa is the standard unit of strength measurement in Shadbala. 1 Rupa equals 60 Virupas."
    },
    {
      q: "Does AstroPredict compute all 6 Shadbala components?",
      a: "Yes! AstroPredict calculates all 6 full components natively using Swiss Ephemeris routines."
    },
    {
      q: "Is this Shadbala Calculator free?",
      a: "Yes, 100%! All calculations on AstroPredict are completely free."
    }
  ];

  const relatedCalculators = [
    {
      title: "Birth Chart Calculator",
      href: "/birth-chart-calculator",
      description: "Generate your complete Kundli with all 12 houses.",
      categoryBadge: "Core"
    },
    {
      title: "Ashtakavarga Calculator",
      href: "/ashtakavarga-calculator",
      description: "Compare 6-fold Shadbala scores with 337-bindu Ashtakavarga house strength.",
      categoryBadge: "Matrix"
    },
    {
      title: "Vimshottari Dasha Calculator",
      href: "/dasha-calculator",
      description: "Evaluate your active Dasha lord's Shadbala Rupa score.",
      categoryBadge: "Timeline"
    }
  ];

  return (
    <div className="space-y-8 max-w-5xl mx-auto py-4">
      {/* Header Banner */}
      <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 shadow-xs flex items-start gap-4">
        <Scale className="w-8 h-8 text-[var(--gold)] shrink-0 mt-1" />
        <div className="space-y-1">
          <h1 className="text-2xl font-serif font-medium text-[var(--foreground)] flex items-center gap-2">
            <span>Complete Shadbala Calculator (Six-Fold Strength)</span>
          </h1>
          <p className="text-xs text-[var(--muted)] leading-relaxed">
            Evaluates all 6 classical planetary strength components: <strong className="text-[var(--foreground)]">Sthana Bala</strong> (Positional), <strong className="text-[var(--foreground)]">Dig Bala</strong> (Directional), <strong className="text-[var(--foreground)]">Kala Bala</strong> (Temporal), <strong className="text-[var(--foreground)]">Chesta Bala</strong> (Motional), <strong className="text-[var(--foreground)]">Naisargika Bala</strong> (Natural), and <strong className="text-[var(--foreground)]">Drik Bala</strong> (Aspectual).
          </p>
        </div>
      </div>

      <QuickCalculatorForm
        title="Enter Birth Details"
        subtitle="Provide exact birth details to evaluate true planetary strengths against classical minimum Rupa thresholds."
        onCalculate={handleCalculate}
        loading={loading}
      />

      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 text-rose-500 text-sm">
          {error}
        </div>
      )}

      {result && (
        <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 shadow-xs space-y-6">
          <div className="flex items-center justify-between border-b border-[var(--line)] pb-4">
            <h2 className="text-xl font-serif font-medium text-[var(--foreground)]">
              Complete 6-Component Planetary Strength Table
            </h2>
            <span className="text-xs font-mono text-emerald-500 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 rounded-full">
              60 Virupas = 1 Rupa
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-[var(--muted)] border-collapse">
              <thead>
                <tr className="border-b border-[var(--line)] bg-[var(--background)] text-[var(--gold)] font-serif font-medium">
                  <th className="py-2.5 px-3">Planet</th>
                  <th className="py-2.5 px-3">Total Virupas</th>
                  <th className="py-2.5 px-3">Total Rupas</th>
                  <th className="py-2.5 px-3">Min Rupa Req</th>
                  <th className="py-2.5 px-3">Ratio %</th>
                  <th className="py-2.5 px-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)] font-mono">
                {result.results.map((r) => (
                  <tr key={r.planet} className="hover:bg-[var(--background)]/50">
                    <td className="py-2.5 px-3 font-semibold text-[var(--foreground)]">{r.planet}</td>
                    <td className="py-2.5 px-3 text-[var(--gold)] font-bold">{r.total_shadbala_virupas.toFixed(1)}</td>
                    <td className="py-2.5 px-3 text-[var(--foreground)] font-bold">{r.total_shadbala_rupas.toFixed(2)}</td>
                    <td className="py-2.5 px-3 text-[var(--muted)]">{r.required_minimum_rupas.toFixed(1)}</td>
                    <td className="py-2.5 px-3 text-emerald-500 font-bold">{(r.strength_ratio * 100).toFixed(0)}%</td>
                    <td className="py-2.5 px-3">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                        {r.classification} ({(r.strength_ratio * 100).toFixed(0)}%)
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CalculatorPageContent
        calculatorName="Shadbala Calculator"
        introParagraph={introParagraph}
        methodologyTitle="How I Calculate Shadbala (Six-Fold Strength)"
        methodologyContent={methodologyContent}
        subsections={subsections}
        faqs={faqs}
        relatedCalculators={relatedCalculators}
        showSignPeriodGrid={false}
      />
    </div>
  );
}
